import { debounce } from "lodash";
import type {
  levelBronzeFormDataType,
  levelBronzeFormDataTypeResponse
} from "@/features/flow/types";
import { fetchServer } from "@/features/utils/fetch";
import { StandardResponse } from "@/features/utils/types";
import { ApiError } from "@/utils/internal-code-handler";
import {
  IdentityDocumentType,
  IdentityDocumentTypeV2
} from "@/views/identity/components/level-card-status-container/verified-company-form/types";
import type {
  CreateKYCDocument,
  CreateKYCSessionData,
  KYCDocument,
  KYCDocumentType,
  KYCSession,
  KYCSessionStatus as KYCSessionState,
  PrepareKYCSessionData,
  PrepareKYCSessionResponse
} from "../types";

export class KYCApiClient {
  static async prepareKYCSession(data: PrepareKYCSessionData): Promise<PrepareKYCSessionResponse> {
    const response = await fetchServer("/kyc_session/prepare_session", {
      method: "POST",
      reloadOn401: false,
      mainRoot: false,
      body: data
    });

    return await response.json();
  }

  static async createKYCSession(data: CreateKYCSessionData): Promise<KYCSession> {
    const response = await fetchServer("/kyc_session/new", {
      method: "POST",
      reloadOn401: false,
      mainRoot: false,
      body: data
    });

    return response.json();
  }
  static editBronzeKYC = debounce(async (data: levelBronzeFormDataType) => {
    const response = await fetchServer("/form_level/bronze", {
      method: "PUT",
      reloadOn401: false,
      mainRoot: false,
      body: data
    });
    if (!response.ok) throw new Error("Error");
    return response.json();
  }, 5000);
  static async getKYCSessionState(data: {
    UUID: string;
    contextLegalEntityUuid?: string;
  }): Promise<KYCSessionState> {
    const response = await fetchServer(
      data.contextLegalEntityUuid
        ? `/kyc_session/state?contextLegalEntityUuid=${data.contextLegalEntityUuid}`
        : "/kyc_session/state",
      {
        method: "POST",
        reloadOn401: false,
        mainRoot: false,
        body: data
      }
    );

    return response.json();
  }

  static async editBronzeKYCWithoutDebounce(data: levelBronzeFormDataType) {
    const response = await fetchServer("/form_level/bronze", {
      method: "PUT",
      reloadOn401: false,
      mainRoot: false,
      body: data
    });
    if (!response.ok) throw new Error("Error");
    return response.json();
  }

  static async editKYCSession(data: CreateKYCDocument) {
    const response = await fetchServer("/kyc_session/edit", {
      method: "POST",
      reloadOn401: false,
      mainRoot: false,
      body: data
    });
    return response.json();
  }

  static async updateDocument(data: CreateKYCDocument) {
    const response = await fetchServer("/kyc_session/document/edit", {
      method: "POST",
      reloadOn401: false,
      mainRoot: false,
      body: data
    });
    return response.json();
  }

  static async getKYCDocuments(data: {
    UUID: string;
    contextLegalEntityUuid?: string;
  }): Promise<KYCDocument[]> {
    const uuid = data.UUID;
    const response = await fetchServer(
      "/document/list?contextLegalEntityUuid=" + data.contextLegalEntityUuid,
      {
        method: "POST",
        reloadOn401: false,
        mainRoot: false,
        body: { uuid }
      }
    );

    if (!response.ok) throw new Error("Error");

    return await response.json();
  }

  static async getKYCDocumentTypes(): Promise<KYCDocumentType[]> {
    const response = await fetchServer("/document/typesByCateg/2", {
      method: "GET",
      reloadOn401: false,
      mainRoot: false
    });

    if (!response.ok) throw new Error("Error");

    return await response.json();
  }

  static async getAllDocumentTypes(): Promise<IdentityDocumentType[]> {
    const response = await fetchServer("/document/types", {
      method: "GET",
      reloadOn401: false,
      mainRoot: false
    });

    if (!response.ok) throw new Error("Error");
    return await response.json();
  }

  static async getAllIdentityDocumentTypesV2(): Promise<{
    success: boolean;
    data: IdentityDocumentTypeV2[];
  }> {
    const response = await fetchServer("/document/identity-document-types", {
      method: "GET",
      reloadOn401: false,
      mainRoot: false
    });

    if (!response.ok) {
      throw new ApiError(await response.json(), response.status);
    }
    return await response.json();
  }

  static async getKycBronzeForm(): Promise<StandardResponse<levelBronzeFormDataTypeResponse>> {
    const response = await fetchServer("/form_level/bronze", {
      method: "GET",
      reloadOn401: false,
      mainRoot: false
    });

    if (!response.ok) throw new ApiError(await response.json(), response.status);

    return await response.json();
  }

  static async updateQuizVideoQuestion(data: CreateKYCDocument) {
    const response = await fetchServer("/kyc_session/quiz/edit", {
      method: "POST",
      reloadOn401: false,
      mainRoot: false,
      body: data
    });
    if (!response.ok) throw new Error("Error");
    return await response.json();
  }
}
